import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, CheckCircle, InfoIcon, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";

export default function DebugScraperPage() {
  const [url, setUrl] = useState("https://www.airbnb.com/rooms/43813369");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("tester");

  const handleScrape = async () => {
    if (!url) {
      setError("Please enter an Airbnb URL");
      return;
    }

    if (!url.includes('airbnb.com')) {
      setError("Please enter a valid Airbnb URL");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      // Call the debug endpoint
      console.log("Sending debug request with URL:", url);
      
      // Add detailed debugging
      try {
        // Use the non-debug endpoint when logged in
        // This is a bit more reliable and gives better results
        const endpoint = 'scrape-listing';
        console.log(`Using endpoint: ${endpoint}`);
        const response = await apiRequest('GET', endpoint);
        
        console.log("Response status:", response.status);
        
        // Convert headers to an object safely
        const headers: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          headers[key] = value;
        });
        console.log("Response headers:", headers);
        
        // Check for content type
        const contentType = response.headers.get('content-type');
        console.log("Content-Type:", contentType);
        
        // Handle non-JSON responses
        if (contentType && !contentType.includes('application/json')) {
          const text = await response.text();
          console.error("Non-JSON response:", text.substring(0, 200));
          throw new Error("Server returned non-JSON response. Server might be in error state.");
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => {
            console.error("Failed to parse error response as JSON");
            return {};
          });
          
          const errorMessage = errorData.error || errorData.message || `Server responded with status: ${response.status}`;
          
          // If we get an error but have diagnostic data, we still want to display it
          if (errorData.debugData) {
            setResult(errorData);
            setActiveTab("diagnostics");
            throw new Error(`${errorMessage} (See diagnostics tab for details)`);
          } else {
            throw new Error(errorMessage);
          }
        }
        
        // Safely parse the JSON response directly from response.json()
        let data;
        try {
          data = await response.json();
          console.log("Debug scraping results:", data);
        } catch (parseError: any) {
          console.error("JSON parse error:", parseError);
          throw new Error(`Failed to parse server response: ${parseError.message}`);
        }
        
        setResult(data);
        setActiveTab(data.property ? "results" : "diagnostics");
      } catch (fetchError: any) {
        console.error("Fetch operation error:", fetchError);
        throw new Error(`Failed to communicate with server: ${fetchError.message}`);
      }
    } catch (err: any) {
      console.error("Scraping error:", err);
      setError(err.message || "Failed to scrape Airbnb listing");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCode = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  return (
    <div className="container mx-auto py-10">
      <h1 className="text-3xl font-bold mb-6">StayMiddle Airbnb Scraper Debug Page</h1>
      <p className="text-muted-foreground mb-8">
        This page allows you to test and debug the Airbnb scraping functionality with detailed diagnostics.
      </p>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tester">Scraper</TabsTrigger>
          <TabsTrigger value="results" disabled={!result || !result.property}>Results</TabsTrigger>
          <TabsTrigger value="diagnostics" disabled={!result}>Diagnostics</TabsTrigger>
        </TabsList>

        <TabsContent value="tester" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Debug Airbnb Scraper</CardTitle>
              <CardDescription>
                Enter an Airbnb URL to test the scraping functionality with enhanced diagnostics.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="url">Airbnb URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://www.airbnb.com/rooms/..."
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleScrape}
                      disabled={isLoading}
                      className="bg-gold hover:bg-gold-dark"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Debugging...
                        </>
                      ) : "Debug Scrape"}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This will test the Airbnb scraping with enhanced diagnostics and detailed error reporting.
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results" className="mt-6 space-y-6">
          {result && result.property && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2 h-5 w-5" />
                    Scraping Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Property Details</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium">Title</p>
                          <p className="text-base">{result.property.title}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Area</p>
                          <p className="text-base">{result.property.area}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Bedrooms</p>
                          <p className="text-base">{result.property.bedrooms}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Bathrooms</p>
                          <p className="text-base">{result.property.bathrooms}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Max Guests</p>
                          <p className="text-base">{result.property.maxGuests}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Base Price</p>
                          <p className="text-base">AED {result.property.basePrice / 100}</p>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">Description</h3>
                      <p className="text-base whitespace-pre-wrap">{result.property.description}</p>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">Features</h3>
                      <div className="flex flex-wrap gap-2">
                        {result.property.features && result.property.features.map((feature: string, index: number) => (
                          <div key={index} className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm">
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <h3 className="text-lg font-semibold mb-2">Images ({result.property.images?.length || 0})</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {result.property.images && result.property.images.map((image: string, index: number) => (
                          <div key={index} className="aspect-square rounded-md overflow-hidden border">
                            <img src={image} alt={`Property image ${index + 1}`} className="object-cover w-full h-full" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="diagnostics" className="mt-6 space-y-6">
          {result && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <InfoIcon className="text-blue-500 mr-2 h-5 w-5" />
                    Scraping Diagnostics
                  </CardTitle>
                  <CardDescription>
                    Detailed information about the scraping process and HTML content
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {result.debugData && (
                      <>
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Overview</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium">URL Tested</p>
                              <p className="text-base">{result.debugData.url}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Content Length</p>
                              <p className="text-base">{result.debugData.contentLength} bytes</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Page Title</p>
                              <p className="text-base">{result.debugData.title || 'None'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Images Found</p>
                              <p className="text-base">{result.debugData.imagesFound || 0}</p>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        <div>
                          <h3 className="text-lg font-semibold mb-2">Security Checks</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium">Has Airbnb Text</p>
                              <p className="text-base">{result.debugData.hasAirbnbText ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Bot Detection</p>
                              <p className="text-base">{result.debugData.allBots ? 'Yes - Bot protection detected' : 'No'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Has Robots</p>
                              <p className="text-base">{result.debugData.hasRobots ? 'Yes' : 'No'}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Has Pictures</p>
                              <p className="text-base">{result.debugData.hasPictures ? 'Yes' : 'No'}</p>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        {result.debugData.propertyDetails && (
                          <div>
                            <h3 className="text-lg font-semibold mb-2">Property Details Found</h3>
                            <div className="grid grid-cols-2 gap-4">
                              {Object.entries(result.debugData.propertyDetails).map(([key, value]) => (
                                <div key={key}>
                                  <p className="text-sm font-medium">{key}</p>
                                  <p className="text-base">{value as string}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {result.debugData.htmlSamples && (
                          <>
                            <Separator />
                            <div>
                              <h3 className="text-lg font-semibold mb-2">HTML Samples</h3>
                              <Tabs defaultValue="head">
                                <TabsList>
                                  <TabsTrigger value="head">Head</TabsTrigger>
                                  <TabsTrigger value="body">Body</TabsTrigger>
                                  <TabsTrigger value="tail">Tail</TabsTrigger>
                                </TabsList>
                                <TabsContent value="head">
                                  <Textarea 
                                    className="font-mono text-xs h-[200px] mt-4" 
                                    readOnly 
                                    value={result.debugData.htmlSamples.head}
                                  />
                                </TabsContent>
                                <TabsContent value="body">
                                  <Textarea 
                                    className="font-mono text-xs h-[200px] mt-4" 
                                    readOnly 
                                    value={result.debugData.htmlSamples.body}
                                  />
                                </TabsContent>
                                <TabsContent value="tail">
                                  <Textarea 
                                    className="font-mono text-xs h-[200px] mt-4" 
                                    readOnly 
                                    value={result.debugData.htmlSamples.tail}
                                  />
                                </TabsContent>
                              </Tabs>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {/* Image sources analysis */}
                    {result.debugData && result.debugData.imageDomains && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Image Sources</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(result.debugData.imageDomains).map(([domain, count]) => (
                            <div key={domain}>
                              <p className="text-sm font-medium">{domain}</p>
                              <p className="text-base">{count as number} images</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Raw Diagnostic Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    readOnly
                    className="font-mono text-xs h-[400px] overflow-auto"
                    value={formatCode(result)}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}